<template>
  <div class="materials-container">
    <div class="page-header">
      <div class="breadcrumb">ຂໍ້ມູນໂຮງຮຽນ / <span>ປຶ້ມແບບຮຽນ ແລະ ອຸປະກອນ</span></div>
      <div class="header-content">
        <h1>ປຶ້ມແບບຮຽນ ແລະ ອຸປະກອນການຮຽນການສອນ</h1>
        <p>ບັນທຶກຂໍ້ມູນສະຖິຕິປຶ້ມ ແລະ ອຸປະກອນ ຕາມຂັ້ນ ແລະ ຕາມວິຊາ</p>
      </div>
    </div>

    <div v-if="loading" class="loading-state-full">
      <i class="fas fa-spinner fa-spin fa-3x"></i>
      <p>ກຳລັງໂຫຼດຂໍ້ມູນ...</p>
    </div>

    <form v-else @submit.prevent="handleSubmit">

      <!-- ===== SCHOOL IDENTITY HEADER ===== -->
      <div class="info-card mb-4">
        <div class="section-label"><i class="fas fa-school"></i> ຂໍ້ມູນລະບຸໂຮງຮຽນ</div>
        <div class="field-row-4">
          <div class="form-group">
            <label class="req">1. ສົກຮຽນ</label>
            <input type="text" v-model="form.academic_year" placeholder="ຕົວຢ່າງ: 2024-2025"
                   pattern="\d{4}-\d{4}" title="ຮູບແບບ: YYYY-YYYY" maxlength="9">
          </div>
          <div class="form-group">
            <label>2. ລະຫັດໂຮງຮຽນ</label>
            <input type="text" :value="schoolInfo.school_id || user.school_id || user.school_code" disabled class="auto-field">
            <span class="auto-badge">AUTO</span>
          </div>
          <div class="form-group">
            <label>3. ຊື່ແຂວງ</label>
            <input type="text" :value="schoolInfo.prov_name || user.prov_name || user.province_name" disabled class="auto-field">
            <span class="auto-badge">AUTO</span>
          </div>
          <div class="form-group">
            <label>4. ຊື່ເມືອງ</label>
            <input type="text" :value="schoolInfo.district_name_lao || user.district_name_lao || user.district_name" disabled class="auto-field">
            <span class="auto-badge">AUTO</span>
          </div>
        </div>
        <div class="field-row-3">
          <div class="form-group">
            <label>5. ຊື່ຕາແສງ</label>
            <input type="text" :value="schoolInfo.commune_name || ''" disabled class="auto-field">
            <span class="auto-badge">AUTO</span>
          </div>
          <div class="form-group">
            <label>6. ຊື່ບ້ານທີ່ໂຮງຮຽນຕັ້ງຢູ່</label>
            <input type="text" :value="schoolInfo.school_village || ''" disabled class="auto-field">
            <span class="auto-badge">AUTO</span>
          </div>
          <div class="form-group">
            <label>7. ຊື່ໂຮງຮຽນ</label>
            <input type="text" :value="schoolInfo.school_name_lao || user.school_name" disabled class="auto-field">
            <span class="auto-badge">AUTO</span>
          </div>
        </div>
      </div>

      <!-- ===== FIELD 8: LEVEL SELECTOR ===== -->
      <div class="level-selector-card mb-4">
        <div class="section-label"><i class="fas fa-layer-group"></i> 8. ຂໍ້ມູນປຶ້ມ ແລະ ອຸປະກອນ ສຳລັບຊັ້ນຮຽນໃດ?</div>
        <div class="level-btns">
          <button type="button" v-for="lv in LEVELS" :key="lv.value"
                  class="level-btn" :class="{ active: form.teaching_level === lv.value }"
                  @click="selectLevel(lv.value)">
            <i :class="lv.icon"></i>
            <span>{{ lv.value }}. {{ lv.label }}</span>
          </button>
        </div>
      </div>

      <!-- ===== TABLE: Pre-primary / Other curriculum (level 1 or 4) ===== -->
      <div v-if="form.teaching_level === 1 || form.teaching_level === 4" class="panel shadow-sm">
        <div class="panel-head">
          <div>
            <h3><i class="fas fa-book"></i>
              {{ form.teaching_level === 1 ? 'ກ່ອນໄວຮຽນ' : 'ຫຼັກສູດອື່ນໆ' }} — ລາຍການປຶ້ມ ແລະ ອຸປະກອນ
            </h3>
            <p class="panel-sub">ຖ້າຄໍາຕອບຂໍ້ 8 = {{ form.teaching_level }}</p>
          </div>
          <div class="panel-stats">
            <span class="stat-pill good">ດີ: {{ totals.pre.good }}</span>
            <span class="stat-pill need">ຕ້ອງການ: {{ totals.pre.needed }}</span>
            <span class="stat-pill get">ໄດ້ຮັບ: {{ totals.pre.received }}</span>
          </div>
        </div>
        <div class="table-wrap">
          <table class="materials-table">
            <thead>
              <tr>
                <th class="col-no">ລຳດັບ</th>
                <th>ລາຍການ ປຶ້ມ / ອຸປະກອນ</th>
                <th class="col-num"><div>9. ສະພາບດີ</div><small>ທ້າຍສົກ</small></th>
                <th class="col-num"><div>10. ຕ້ອງການ</div><small>ສົກໜ້າ</small></th>
                <th class="col-num"><div>11. ໄດ້ຮັບ</div><small>ສົກນີ້</small></th>
                <th class="col-remain"><div>ຍັງ</div><small>ດີ+ຮັບ−ຕ້ອງ</small></th>
                <th class="col-del"></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="grade in PRE_PRIMARY_GRADES">
                <tr class="grade-header-row" :key="'h-' + grade">
                  <td colspan="7">
                    <i class="fas fa-layer-group"></i> ຊັ້ນ {{ grade }}
                    <span class="grade-subtotals">
                      ດີ: <strong>{{ gradeSum(form.pre_primary_books, grade).good }}</strong>
                      ຕ້ອງການ: <strong>{{ gradeSum(form.pre_primary_books, grade).needed }}</strong>
                      ໄດ້ຮັບ: <strong>{{ gradeSum(form.pre_primary_books, grade).received }}</strong>
                      ຍັງ: <strong :class="gradeSum(form.pre_primary_books, grade).remain < 0 ? 'remain-neg' : gradeSum(form.pre_primary_books, grade).remain > 0 ? 'remain-pos' : 'remain-zero'">{{ gradeSum(form.pre_primary_books, grade).remain }}</strong>
                    </span>
                  </td>
                </tr>
                <tr v-for="(item, idx) in form.pre_primary_books[grade]" :key="grade + '-' + idx"
                    :class="{ 'low-stock': item.good_condition === 0 && item.fixed }">
                  <td class="col-no text-center">{{ idx + 1 }}</td>
                  <td>
                    <span v-if="item.fixed" class="book-name">{{ item.name }}</span>
                    <input v-else type="text" v-model="item.name" placeholder="ຊື່ປຶ້ມ / ອຸປະກອນ" class="name-input">
                    <span v-if="item.good_condition === 0 && item.fixed" class="low-badge">
                      <i class="fas fa-exclamation-triangle"></i> ໝົດ/ຂາດ
                    </span>
                  </td>
                  <td class="col-num"><input type="number" v-model.number="item.good_condition" min="0" class="num-input"></td>
                  <td class="col-num"><input type="number" v-model.number="item.needed" min="0" class="num-input"></td>
                  <td class="col-num"><input type="number" v-model.number="item.received" min="0" class="num-input"></td>
                  <td class="col-remain text-center" :class="remainClass(item)"><strong>{{ remain(item) }}</strong></td>
                  <td class="col-del text-center">
                    <button v-if="!item.fixed" type="button" class="btn-del" @click="removePreBook(grade, idx)" title="ລຶບ">
                      <i class="fas fa-times"></i>
                    </button>
                  </td>
                </tr>
                <tr class="add-grade-row" :key="'add-' + grade">
                  <td colspan="7">
                    <button type="button" class="btn-add-grade" @click="addPreBook(grade)">
                      <i class="fas fa-plus"></i> ເພີ່ມລາຍການ {{ grade }}
                    </button>
                  </td>
                </tr>
              </template>
            </tbody>
            <tfoot>
              <tr class="total-row">
                <td colspan="2" class="text-right"><strong>ລວມທັງໝົດ</strong></td>
                <td class="text-center"><strong>{{ totals.pre.good }}</strong></td>
                <td class="text-center"><strong>{{ totals.pre.needed }}</strong></td>
                <td class="text-center"><strong>{{ totals.pre.received }}</strong></td>
                <td class="text-center" :class="totals.pre.remain < 0 ? 'remain-neg' : totals.pre.remain > 0 ? 'remain-pos' : 'remain-zero'"><strong>{{ totals.pre.remain }}</strong></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- ===== TABLE: Primary (level 2) ===== -->
      <div v-if="form.teaching_level === 2" class="panel shadow-sm">
        <div class="panel-head">
          <div>
            <h3><i class="fas fa-book"></i> ປະຖົມ — ລາຍການປຶ້ມ ແລະ ອຸປະກອນ</h3>
            <p class="panel-sub">ຖ້າຄໍາຕອບຂໍ້ 8 = 2</p>
          </div>
          <div class="panel-stats">
            <span class="stat-pill good">ດີ: {{ totals.primary.good }}</span>
            <span class="stat-pill need">ຕ້ອງການ: {{ totals.primary.needed }}</span>
            <span class="stat-pill get">ໄດ້ຮັບ: {{ totals.primary.received }}</span>
          </div>
        </div>
        <div class="table-wrap">
          <table class="materials-table">
            <thead>
              <tr>
                <th class="col-no">ລຳດັບ</th>
                <th>ລາຍການ ປຶ້ມ / ວິຊາ</th>
                <th class="col-num"><div>ສະພາບດີ</div><small>ທ້າຍສົກ</small></th>
                <th class="col-num"><div>ຕ້ອງການ</div><small>ສົກໜ້າ</small></th>
                <th class="col-num"><div>ໄດ້ຮັບ</div><small>ສົກນີ້</small></th>
                <th class="col-remain"><div>ຍັງ</div><small>ດີ+ຮັບ−ຕ້ອງ</small></th>
                <th class="col-del"></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="grade in PRIMARY_GRADES">
                <tr class="grade-header-row" :key="'h-' + grade">
                  <td colspan="7">
                    <i class="fas fa-layer-group"></i> ຊັ້ນ {{ grade }}
                    <span class="grade-subtotals">
                      ດີ: <strong>{{ gradeSum(form.primary_books, grade).good }}</strong>
                      ຕ້ອງການ: <strong>{{ gradeSum(form.primary_books, grade).needed }}</strong>
                      ໄດ້ຮັບ: <strong>{{ gradeSum(form.primary_books, grade).received }}</strong>
                      ຍັງ: <strong :class="gradeSum(form.primary_books, grade).remain < 0 ? 'remain-neg' : gradeSum(form.primary_books, grade).remain > 0 ? 'remain-pos' : 'remain-zero'">{{ gradeSum(form.primary_books, grade).remain }}</strong>
                    </span>
                  </td>
                </tr>
                <tr v-for="(item, idx) in form.primary_books[grade]" :key="grade + '-' + idx"
                    :class="{ 'low-stock': item.good_condition === 0 && item.fixed }">
                  <td class="col-no text-center">{{ idx + 1 }}</td>
                  <td>
                    <span v-if="item.fixed" class="book-name">{{ item.name }}</span>
                    <input v-else type="text" v-model="item.name" placeholder="ຊື່ປຶ້ມ / ວິຊາ / ອຸປະກອນ" class="name-input">
                    <span v-if="item.good_condition === 0 && item.fixed" class="low-badge">
                      <i class="fas fa-exclamation-triangle"></i> ໝົດ/ຂາດ
                    </span>
                  </td>
                  <td class="col-num"><input type="number" v-model.number="item.good_condition" min="0" class="num-input"></td>
                  <td class="col-num"><input type="number" v-model.number="item.needed" min="0" class="num-input"></td>
                  <td class="col-num"><input type="number" v-model.number="item.received" min="0" class="num-input"></td>
                  <td class="col-remain text-center" :class="remainClass(item)"><strong>{{ remain(item) }}</strong></td>
                  <td class="col-del text-center">
                    <button v-if="!item.fixed" type="button" class="btn-del" @click="removePrimaryBook(grade, idx)" title="ລຶບ">
                      <i class="fas fa-times"></i>
                    </button>
                  </td>
                </tr>
                <tr class="add-grade-row" :key="'add-' + grade">
                  <td colspan="7">
                    <button type="button" class="btn-add-grade" @click="addPrimaryBook(grade)">
                      <i class="fas fa-plus"></i> ເພີ່ມລາຍການ {{ grade }}
                    </button>
                  </td>
                </tr>
              </template>
            </tbody>
            <tfoot>
              <tr class="total-row">
                <td colspan="2" class="text-right"><strong>ລວມທັງໝົດ</strong></td>
                <td class="text-center"><strong>{{ totals.primary.good }}</strong></td>
                <td class="text-center"><strong>{{ totals.primary.needed }}</strong></td>
                <td class="text-center"><strong>{{ totals.primary.received }}</strong></td>
                <td class="text-center" :class="totals.primary.remain < 0 ? 'remain-neg' : totals.primary.remain > 0 ? 'remain-pos' : 'remain-zero'"><strong>{{ totals.primary.remain }}</strong></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- ===== TABLE: Secondary (level 3) ===== -->
      <div v-if="form.teaching_level === 3" class="panel shadow-sm">
        <div class="panel-head">
          <div>
            <h3><i class="fas fa-book"></i> ມັດທະຍົມ — ລາຍການວິຊາ ແລະ ປຶ້ມຮຽນ</h3>
            <p class="panel-sub">ຖ້າຄໍາຕອບຂໍ້ 8 = 3</p>
          </div>
          <div class="panel-stats">
            <span class="stat-pill good">ດີ: {{ totals.secondary.good }}</span>
            <span class="stat-pill need">ຕ້ອງການ: {{ totals.secondary.needed }}</span>
            <span class="stat-pill get">ໄດ້ຮັບ: {{ totals.secondary.received }}</span>
          </div>
        </div>
        <div class="table-wrap">
          <table class="materials-table">
            <thead>
              <tr>
                <th class="col-no">ລຳດັບ</th>
                <th>ລາຍການ ວິຊາ / ປຶ້ມ</th>
                <th class="col-num"><div>ສະພາບດີ</div><small>ທ້າຍສົກ</small></th>
                <th class="col-num"><div>ຕ້ອງການ</div><small>ສົກໜ້າ</small></th>
                <th class="col-num"><div>ໄດ້ຮັບ</div><small>ສົກນີ້</small></th>
                <th class="col-remain"><div>ຍັງ</div><small>ດີ+ຮັບ−ຕ້ອງ</small></th>
                <th class="col-del"></th>
              </tr>
            </thead>
            <tbody>
              <template v-for="grade in SECONDARY_GRADES">
                <tr class="grade-header-row" :key="'h-' + grade">
                  <td colspan="7">
                    <i class="fas fa-layer-group"></i> ຊັ້ນ {{ grade }}
                    <span class="grade-subtotals">
                      ດີ: <strong>{{ gradeSum(form.secondary_books, grade).good }}</strong>
                      ຕ້ອງການ: <strong>{{ gradeSum(form.secondary_books, grade).needed }}</strong>
                      ໄດ້ຮັບ: <strong>{{ gradeSum(form.secondary_books, grade).received }}</strong>
                      ຍັງ: <strong :class="gradeSum(form.secondary_books, grade).remain < 0 ? 'remain-neg' : gradeSum(form.secondary_books, grade).remain > 0 ? 'remain-pos' : 'remain-zero'">{{ gradeSum(form.secondary_books, grade).remain }}</strong>
                    </span>
                  </td>
                </tr>
                <tr v-for="(item, idx) in form.secondary_books[grade]" :key="grade + '-' + idx"
                    :class="{ 'low-stock': item.good_condition === 0 && item.fixed }">
                  <td class="col-no text-center">{{ idx + 1 }}</td>
                  <td>
                    <span v-if="item.fixed" class="book-name">{{ item.name }}</span>
                    <input v-else type="text" v-model="item.name" placeholder="ຊື່ວິຊາ / ປຶ້ມ" class="name-input">
                    <span v-if="item.good_condition === 0 && item.fixed" class="low-badge">
                      <i class="fas fa-exclamation-triangle"></i> ໝົດ/ຂາດ
                    </span>
                  </td>
                  <td class="col-num"><input type="number" v-model.number="item.good_condition" min="0" class="num-input"></td>
                  <td class="col-num"><input type="number" v-model.number="item.needed" min="0" class="num-input"></td>
                  <td class="col-num"><input type="number" v-model.number="item.received" min="0" class="num-input"></td>
                  <td class="col-remain text-center" :class="remainClass(item)"><strong>{{ remain(item) }}</strong></td>
                  <td class="col-del text-center">
                    <button v-if="!item.fixed" type="button" class="btn-del" @click="removeSecBook(grade, idx)" title="ລຶບ">
                      <i class="fas fa-times"></i>
                    </button>
                  </td>
                </tr>
                <tr class="add-grade-row" :key="'add-' + grade">
                  <td colspan="7">
                    <button type="button" class="btn-add-grade" @click="addSecBook(grade)">
                      <i class="fas fa-plus"></i> ເພີ່ມລາຍການ {{ grade }}
                    </button>
                  </td>
                </tr>
              </template>
            </tbody>
            <tfoot>
              <tr class="total-row">
                <td colspan="2" class="text-right"><strong>ລວມທັງໝົດ</strong></td>
                <td class="text-center"><strong>{{ totals.secondary.good }}</strong></td>
                <td class="text-center"><strong>{{ totals.secondary.needed }}</strong></td>
                <td class="text-center"><strong>{{ totals.secondary.received }}</strong></td>
                <td class="text-center" :class="totals.secondary.remain < 0 ? 'remain-neg' : totals.secondary.remain > 0 ? 'remain-pos' : 'remain-zero'"><strong>{{ totals.secondary.remain }}</strong></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- Remarks -->
      <div v-if="form.teaching_level" class="panel shadow-sm mt-4">
        <div class="panel-body">
          <div class="form-group">
            <label>ໝາຍເຫດ</label>
            <textarea v-model="form.remarks" rows="3" placeholder="ລະບຸລາຍລະອຽດເພີ່ມເຕີມ ຫຼື ແຫຼ່ງທຶນທີ່ໄດ້ຮັບປຶ້ມ..."></textarea>
          </div>
        </div>
      </div>

      <div class="form-actions-wrapper mt-4">
        <button type="submit" class="btn-save" :disabled="submitting || !form.teaching_level">
          <i class="fas" :class="submitting ? 'fa-spinner fa-spin' : 'fa-save'"></i>
          {{ submitting ? 'ກຳລັງບັນທຶກ...' : 'ບັນທຶກຂໍ້ມູນທັງໝົດ' }}
        </button>
      </div>
    </form>

    <!-- Result Modal -->
    <transition name="modal">
      <div v-if="resultModal.show" class="modal-overlay" @mousedown.self="resultModal.show = false">
        <div class="custom-modal-card result-modal">
          <div class="modal-icon" :class="resultModal.type">
            <i class="fas" :class="resultModal.type === 'success' ? 'fa-check-circle' : 'fa-times-circle'"></i>
          </div>
          <div class="modal-content-area">
            <h2>{{ resultModal.title }}</h2>
            <p>{{ resultModal.message }}</p>
          </div>
          <div class="modal-footer-actions">
            <button class="btn-modal-primary" :class="resultModal.type" @click="resultModal.show = false">ເຂົ້າໃຈແລ້ວ</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import api from '@/utils/api';

// ======================================================
// ລາຍການປຶ້ມ ກ່ອນໄວຮຽນ / ຫຼັກສູດອື່ນ (9.1 – 9.54)
// ======================================================
const PRE_PRIMARY_BOOK_NAMES = [
  "ແຜນການລ້ຽງດູ ແລະ ສຶກສາອົບຮົມເດັກ ອາຍຸ 3 ຫາ 36 ເດືອນ",
  "ປຶ້ມຄູ່ມືຄູສອນຊັ້ນອະນຸບານ",
  "ປຶ້ມແຜນຈັດປະສົບການ",
  "ປຶ້ມຮຽກຊື່ ແລະ ບັນທຶກພັດທະນາການເດັກ",
  "ປຶ້ມຕິດຕາມພັດທະນາການເດັກປະຈຳພາກຮຽນ",
  "ປຶ້ມຕິດຕາມຜົນງານສິລະປະສ້າງສັນປະຈຳເດືອນ",
  "ປຶ້ມບົດຝຶກຫັດທັງ 8 ໜ່ວຍການສອນ",
  "ປຶ້ມຜະລິດເກມການສຶກສາ",
  "ໂດມິໂນ",
  "ໝວດເກມ",
  "ໝວດບ໋ອກ",
  "ໝວດອຸປະກອນ",
  "ໝວດສື່ການຮຽນ-ການສອນ",
  "ໝວດເຄື່ອງຫຼິ້ນກາງແຈ້ງ",
  "ປຶ້ມບົດຝຶກຫັດຄະນິດສາດ 1",
  "ປຶ້ມບົດຝຶກຫັດພາສາລາວ 1",
  "ປຶ້ມບົດຝຶກຫັດຄະນິດສາດ 2",
  "ປຶ້ມບົດຝຶກຫັດພາສາລາວ 2",
   "ບົດຝຶກຫັດ ໜ່ວຍຕົວຂອງຂ້ອຍ",
  "ບົດຝຶກຫັດ ຄອບຄົວຂອງຂ້ອຍ",
  "ບົດຝຶກຫັດ ໜ່ວຍໂຮງຮຽນຂອງຂ້ອຍ",
  "ບົດຝຶກຫັດ ໜ່ວຍບ້ານຂອງຂ້ອຍ",
  "ບົດຝຶກຫັດ ໜ່ວຍທຳມະຊາດອ້ອມຕົວເຮົາ",
  "ບົດຝຶກຫັດ ໜ່ວຍສິ່ງແວດລ້ອມອ້ອມຕົວ",
  "ບົດຝຶກຫັດ ໜ່ວຍຄົມມະນາຄົມ ແລະ ການສື່ສານ",
  "ບົດຝຶກຫັດ ໜ່ວຍສິ່ງຕ່າງໆອ້ອມຕົວເຮົາ",
  "ໜ່ວຍ ຕົວຂອງຂ້ອຍ",
  "ໜ່ວຍ ຄອບຄົວຂອງຂ້ອຍ",
  "ໜ່ວຍ ບ້ານຂອງຂ້ອຍ",
  "ໜ່ວຍ ທຳມະຊາດອ້ອມຕົວເຮົາ",
  "ໜ່ວຍ ສິ່ງແວດລ້ອມອ້ອມຕົວເຮົາ",
  "ໜ່ວຍ ຄົມມະນາຄົມ ແລະ ການສື່ສານ",
  "ປຶ້ມບົດເລື່ອງ \"ເຈົ້າເໝົາ\"",
  "ບົດເລື່ອງ \"ຕະກຽງນ້ອຍກັບດວງອາທິດ\"",
  "ໜ່ວຍ ສິ່ງຕ່າງໆອ້ອມຕົວເຮົາ",
  "ປຶ້ມຄູ່ມື 6 ກິດຈະກຳ",
  "ຄູ່ມືກິດຈະກຳການເຄື່ອນໄຫວ ແລະ ຈັງຫວະ",
  "ຄູ່ມືກິດຈະກຳວົງມົນ",
  "ຄູ່ມືກິດຈະກຳສິລະປະສ້າງສັນ",
  "ຄູ່ມືກິດຈະກຳກາງແຈ້ງ",
  "ຄູ່ມືກິດຈະກຳເກມການສຶກສາ",
  "ຄູ່ມືກິດຈະກຳຫຼິ້ນຕາມມຸມ",
  "ຄູ່ມືກິດຈະກຳການຮຽນ-ການສອນ ແລະ ຕິດຕາມປະເມີນຜົນ",
  "ຄູ່ມືສຳລັບຜູ້ດູແລເດັກ ໜ່ວຍການສອນທີ 1 \"ຮ່າງກາຍຂອງຂ້ອຍ\"",
  "ຄູ່ມືສຳລັບຜູ້ດູແລເດັກ ໜ່ວຍການສອນທີ 2 \"ຄອບຄົວຂອງຂ້ອຍ\"",
  "ຄູ່ມືສຳລັບຜູ້ດູແລເດັກ ໜ່ວຍການສອນທີ 3 \"ໂຮງຮຽນຂອງຂ້ອຍ\"",
  "ຄູ່ມືສຳລັບຜູ້ດູແລເດັກ ໜ່ວຍການສອນທີ 4 \"ບ້ານຂອງຂ້ອຍ\"",
  "ຄູ່ມືສຳລັບຜູ້ດູແລເດັກ ໜ່ວຍການສອນທີ 5 \"ສິ່ງທີ່ມີຊີວິດໃນໂລກຂອງຂ້ອຍ\"",
  "ຄູ່ມືສຳລັບຜູ້ດູແລເດັກ ໜ່ວຍການສອນທີ 6 \"ສິ່ງບໍ່ມີຊີວິດໃນໂລກຂອງຂ້ອຍ\"",
  "ຄູ່ມືສຳລັບຜູ້ດູແລເດັກ ໜ່ວຍການສອນທີ 7 \"ໂລກອ້ອມຕົວຂອງຂ້ອຍ\"",
  "ຄູ່ມືສຳລັບຜູ້ດູແລເດັກ ໜ່ວຍການສອນທີ 8 \"ການສື່ສານ\"",
  "ຄູ່ມືແນະນຳການນຳໃຊ້ວັດສະດຸທ້ອງຖິ່ນເຂົ້າໃນການຮຽນການສອນສຳລັບຊັ້ນອະນຸບານຜ່ານການຫຼິ້ນ",
  "ປຶ້ມອື່ນໆ"
];

// ======================================================
// ລາຍການ ມັດທະຍົມ
// ======================================================
const SECONDARY_BOOK_NAMES = [
  "ແບບຮຽນ ພາສາລາວ",
  "ແບບຮຽນ ວັນນະຄະດີ",
  "ແບບຮຽນ ຄະນິດສາດ",
  "ແບບຮຽນ ຟີຊິກສາດ",
  "ແບບຮຽນ ເຄມີສາດ",
  "ແບບຮຽນ ເລຂາຄະນິດ",
  "ແບບຮຽນ ຊີວະວິທະຍາ",
  "ແບບຮຽນ ສຶກສາພົນລະເມືອງ",
  "ແບບຮຽນ ປະຫວັດສາດ",
  "ແບບຮຽນ ພູມສາດ",
  "ແບບຮຽນ ເຕັກໂນໂລຊີ",
  "ແບບຮຽນ ເຕັກໂນໂລຊີ ຂໍ້ມູນຂ່າວສານ ແລະ ການສື່ສານ (ICT)",
  "ແບບຮຽນ ພະລະສຶກສາ",
  "ແບບຮຽນ ສິລະປະດົນຕີ",
  "ແບບຮຽນ ສິລະປະກຳ ແລະ ຫັດຖະກຳ",
  "ແບບຮຽນ ພາສາອັງກິດ",
  "ແບບຮຽນ ພາສາຝຣັ່ງ",
  "ແບບຮຽນ ສັດຕະສາດ",
  "ແບບຮຽນ ສຸຂະສຶກສາ",
  "ແບບຮຽນ ທຸລະກິດ",
  "ແບບຮຽນ ທຸລະກິດຮຽນຄວບ 6 ສາຂາວິຊາ",
  "ສາຂາວິຊາ ຊ່າງໄຟຟ້າ",
  "ສາຂາວິຊາ ກໍ່ສ້າງເຄຫະສະຖານ",
  "ສາຂາວິຊາ ໂລຫະວິທະຍາ",
  "ສາຂາວິຊາ ຊ່າງລົດຍົນ",
  "ສາຂາວິຊາ ການເຮືອນ",
  "ຄູ່ມືຄູ ພາສາລາວ",
  "ຄູ່ມືຄູ ວັນນະຄະດີ",
  "ຄູ່ມືຄູ ຄະນິດສາດ",
  "ຄູ່ມືຄູ ຟີຊິກສາດ",
  "ຄູ່ມືຄູ ເຄມີສາດ",
  "ຄູ່ມືຄູ ເລຂາຄະນິດ",
  "ຄູ່ມືຄູ ຊີວະວິທະຍາ",
  "ຄູ່ມືຄູ ສຶກສາພົນລະເມືອງ",
  "ຄູ່ມືຄູ ປະຫວັດສາດ",
  "ຄູ່ມືຄູ ພູມສາດ",
  "ຄູ່ມືຄູ ເຕັກໂນໂລຊີ",
  "ຄູ່ມືຄູ ເຕັກໂນໂລຊີ ຂໍ້ມູນຂ່າວສານ ແລະ ການສື່ສານ (ICT)",
  "ຄູ່ມືຄູ ພະລະສຶກສາ",
  "ຄູ່ມືຄູ ສິລະປະດົນຕີ",
  "ຄູ່ມືຄູ ສິລະປະກຳ ແລະ ຫັດຖະກຳ",
  "ຄູ່ມືຄູ ພາສາອັງກິດ",
  "ຄູ່ມືຄູ ພາສາຝຣັ່ງ",
  "ຄູ່ມືຄູ ສັດຕະສາດ",
  "ຄູ່ມືຄູ ສຸຂະສຶກສາ",
  "ຄູ່ມືຄູ ວິຊາທຸລະກິດ",
  "ຄູ່ມືຄູ ທຸລະກິດຮຽນຄວບ 6 ສາຂາວິຊາ",
  "ຄູ່ມືຄູ ສາຂາວິຊາ ກະສິກຳ",
  "ຄູ່ມືຄູ ກໍ່ສ້າງເຄຫະສະຖານ",
  "ຄູ່ມືຄູ ໂລຫະວິທະຍາ",
  "ຄູ່ມືຄູ ຊ່າງລົດຍົນ",
  "ຄູ່ມືຄູ ການເຮືອນ",
  "ປຶ້ມອື່ນໆ"
];

// ======================================================
// ລາຍການປຶ້ມ ປະຖົມ ປ 1–5 (ແລະ ປ 6 ຖ້ານຳໃຊ້)
// ======================================================
const PRIMARY_BOOK_NAMES = [
  "ແບບຮຽນ ພາສາລາວ",
  "ແບບຮຽນ ພາສາລາວ ເຫຼັ້ມ 1",
  "ແບບຮຽນ ພາສາລາວ ເຫຼັ້ມ 2",
  "ແບບຮຽນ ຄະນິດສາດ",
  "ແບບຮຽນ ວິທະຍາສາດ ແລະ ສິ່ງແວດລ້ອມ",
  "ແບບຮຽນ ຄຸນສົມບັດສຶກສາ",
  "ແບບຮຽນ ພາສາອັງກິດ",
  "ແບບຮຽນ ສັງຄົມສຶກສາ",
  "ແບບຮຽນ ສິລະປະກຳ ແລະ ຫັດຖະກຳ",
  "ແບບຮຽນ ສິລະປະດົນຕີ",
  "ແບບຮຽນ ພະລະສຶກສາ",
  "ຄູ່ມືຄູ ພາສາລາວ",
  "ຄູ່ມືຄູ ພາສາລາວ ເຫຼັ້ມ 1",
  "ຄູ່ມືຄູ ພາສາລາວ ເຫຼັ້ມ 2",
  "ຄູ່ມືຄູ ຄະນິດສາດ",
  "ຄູ່ມືຄູ ວິທະຍາສາດ ແລະ ສິ່ງແວດລ້ອມ",
  "ຄູ່ມືຄູ ຄຸນສົມບັດສຶກສາ",
  "ຄູ່ມືຄູ ພາສາອັງກິດ",
  "ຄູ່ມືຄູ ສັງຄົມສຶກສາ",
  "ຄູ່ມືຄູ ສິລະປະກຳ ແລະ ຫັດຖະກຳ",
  "ຄູ່ມືຄູ ສິລະປະດົນຕີ",
  "ຄູ່ມືຄູ ພະລະສຶກສາ",
  "ປຶ້ມອື່ນໆ",
  "ແບບຮຽນ ການສຶກສາຂັ້ນພື້ນຖານ ນອກໂຮງຮຽນ ລະດັບ I",
  "ແບບຮຽນ ການສຶກສາຂັ້ນພື້ນຖານ ນອກໂຮງຮຽນ ລະດັບ II",
  "ແບບຮຽນ ການສຶກສາຂັ້ນພື້ນຖານ ນອກໂຮງຮຽນ ລະດັບ III",
  "ຄູ່ມື ການສຶກສາຂັ້ນພື້ນຖານ ນອກໂຮງຮຽນ ລະດັບ I",
  "ຄູ່ມື ການສຶກສາຂັ້ນພື້ນຖານ ນອກໂຮງຮຽນ ລະດັບ II",
  "ຄູ່ມື ການສຶກສາຂັ້ນພື້ນຖານ ນອກໂຮງຮຽນ ລະດັບ III",
  "ໂຕະ ສຳລັບນັກຮຽນ",
  "ແປ້ນມ້າ ສຳລັບນັກຮຽນ",
  "ຕັ່ງອີ້ ສຳລັບນັກຮຽນ",
  "ກະດານດຳ/ກະດານຂາວ",
  "ຕູ້ ສຳລັບນັກຮຽນ",
  "ກະດານແຜ່ນນ້ອຍ",
  "ອຸປະກອນຮຽນພາສາ",
  "ອຸປະກອນຄະນິດສາດ",
  "ອຸປະກອນແຕ້ມ",
  "ປຶ້ມຫຼັກສູດ",
  "ປຶ້ມຮຽກຊື່ ແລະ ໃຫ້ຄະແນນ",
  "ປຶ້ມຕິດຕາມ ການສອນ",
  "ປຶ້ມຕິດຕາມ ການຮຽນ",
  "ປຶ້ມແຕ່ງບົດສອນຂອງຄູ"
];

const PRE_PRIMARY_GRADES = ['ອ1', 'ອ2', 'ອ3'];
const PRIMARY_GRADES     = ['ປ1', 'ປ2', 'ປ3', 'ປ4', 'ປ5', 'ປ6','ປ7'];
const SECONDARY_GRADES   = ['ມ1', 'ມ2', 'ມ3', 'ມ4', 'ມ5', 'ມ6', 'ມ7'];

function makeBookRow(name, fixed = true) {
  return { name, fixed, good_condition: 0, needed: 0, received: 0 };
}

function initGradeBooks(bookNames, grades, saved) {
  // migrate: if saved is old flat array, discard it (start fresh)
  const src = (saved && !Array.isArray(saved)) ? saved : {};
  return grades.reduce((obj, grade) => {
    const savedArr = src[grade] || [];
    obj[grade] = bookNames.map((name, i) => {
      const s = savedArr[i] || {};
      return { name, fixed: true, good_condition: s.good_condition || 0, needed: s.needed || 0, received: s.received || 0 };
    }).concat((savedArr.slice(bookNames.length) || []).map(s => ({ ...s, fixed: false })));
    return obj;
  }, {});
}
function initPrePrimaryBooks(saved) {
  return initGradeBooks(PRE_PRIMARY_BOOK_NAMES, PRE_PRIMARY_GRADES, saved);
}
function initPrimaryBooks(saved) {
  return initGradeBooks(PRIMARY_BOOK_NAMES, PRIMARY_GRADES, saved);
}
function initSecondaryBooks(saved) {
  return initGradeBooks(SECONDARY_BOOK_NAMES, SECONDARY_GRADES, saved);
}

function sumGradeBooks(gradeObj) {
  const all = Object.values(gradeObj || {}).flat();
  return all.reduce((a, b) => ({
    good:    a.good    + (parseInt(b.good_condition) || 0),
    needed:  a.needed  + (parseInt(b.needed)         || 0),
    received:a.received+ (parseInt(b.received)        || 0),
    remain:  a.remain  + ((parseInt(b.good_condition)||0) + (parseInt(b.received)||0) - (parseInt(b.needed)||0))
  }), { good: 0, needed: 0, received: 0, remain: 0 });
}
function sumArr(arr) {
  return (arr || []).reduce((a, b) => ({
    good:    a.good    + (parseInt(b.good_condition) || 0),
    needed:  a.needed  + (parseInt(b.needed)         || 0),
    received:a.received+ (parseInt(b.received)        || 0),
    remain:  a.remain  + ((parseInt(b.good_condition)||0) + (parseInt(b.received)||0) - (parseInt(b.needed)||0))
  }), { good: 0, needed: 0, received: 0, remain: 0 });
}

export default {
  name: 'SchoolMaterialsPage',
  data() {
    return {
      loading: false,
      submitting: false,
      isNew: true,
      user: {},
      PRE_PRIMARY_GRADES,
      PRIMARY_GRADES,
      SECONDARY_GRADES,
      schoolInfo: { school_id: '', prov_name: '', district_name_lao: '', school_name_lao: '', commune_name: '', school_village: '' },
      LEVELS: [
        { value: 1, label: 'ກ່ອນໄວຮຽນ', icon: 'fas fa-child' },
        { value: 2, label: 'ປະຖົມ', icon: 'fas fa-graduation-cap' },
        { value: 3, label: 'ມັດທະຍົມ', icon: 'fas fa-university' },
        { value: 4, label: 'ຫຼັກສູດອື່ນໆ', icon: 'fas fa-book-open' }
      ],
      form: {
        school_id: '',
        academic_year: '',
        commune_name: '',
        school_village: '',
        teaching_level: null,
        pre_primary_books: initPrePrimaryBooks(),
        primary_books: initPrimaryBooks(),
        secondary_books: initSecondaryBooks(),
        remarks: ''
      },
      resultModal: { show: false, type: 'success', title: '', message: '' }
    };
  },
  computed: {
    totals() {
      return {
        pre:       sumGradeBooks(this.form.pre_primary_books),
        primary:   sumGradeBooks(this.form.primary_books),
        secondary: sumGradeBooks(this.form.secondary_books)
      };
    }
  },
  async mounted() {
    this.user = JSON.parse(localStorage.getItem('user-info') || '{}');
    await Promise.all([this.fetchSchoolInfo(), this.fetchData()]);
  },
  methods: {
    async fetchSchoolInfo() {
      if (!this.user.school_id) return;
      try {
        const res = await api.get(`/getAll/schools/school_id/${this.user.school_id}`);
        if (res.data.code === 1 && res.data.rows.length > 0) {
          const s = res.data.rows[0];
          this.schoolInfo = {
            school_id: s.school_id || this.user.school_id,
            prov_name: s.prov_name || '',
            district_name_lao: s.district_name_lao || '',
            school_name_lao: s.school_name_lao || '',
            commune_name: s.commune_name || '',
            school_village: s.school_village || '',
          };
        }
      } catch (e) { console.error(e); }
    },
    remain(item) {
      return (parseInt(item.good_condition) || 0) + (parseInt(item.received) || 0) - (parseInt(item.needed) || 0);
    },
    remainClass(item) {
      const v = this.remain(item);
      return v < 0 ? 'remain-neg' : v === 0 ? 'remain-zero' : 'remain-pos';
    },
    gradeSum(gradeObj, grade) {
      return sumArr(gradeObj[grade]);
    },
    selectLevel(val) {
      this.form.teaching_level = val;
    },
    addPreBook(grade) {
      this.form.pre_primary_books[grade].push(makeBookRow('', false));
    },
    removePreBook(grade, i) {
      this.form.pre_primary_books[grade].splice(i, 1);
    },
    addPrimaryBook(grade) {
      this.form.primary_books[grade].push(makeBookRow('', false));
    },
    removePrimaryBook(grade, i) {
      this.form.primary_books[grade].splice(i, 1);
    },
    addSecBook(grade) {
      this.form.secondary_books[grade].push(makeBookRow('', false));
    },
    removeSecBook(grade, i) {
      this.form.secondary_books[grade].splice(i, 1);
    },
    async fetchData() {
      if (!this.user.school_id) return;
      this.loading = true;
      try {
        const res = await api.get(`/getAll/school_materials/school_id/${this.user.school_id}`);
        if (res.data.code === 1 && res.data.rows.length > 0) {
          const d = res.data.rows[0];
          this.form = {
            ...this.form,
            ...d,
            pre_primary_books: initPrePrimaryBooks(d.pre_primary_books || {}),
            secondary_books:   initSecondaryBooks(d.secondary_books   || {}),
            primary_books:     initPrimaryBooks(d.primary_books       || {})
          };
          this.isNew = false;
        } else {
          this.form.school_id = this.user.school_id;
          this.isNew = true;
        }
      } catch (e) { console.error(e); }
      finally { this.loading = false; }
    },
    showResult(type, title, message) {
      this.resultModal = { show: true, type, title, message };
    },
    async handleSubmit() {
      this.submitting = true;
      try {
        let res;
        if (this.isNew) {
          res = await api.post('/insertOne', { table: 'school_materials', items: this.form });
        } else {
          res = await api.put(`/update/school_materials/${this.form._id}`, { items: this.form });
        }
        if (res.data.code === 1) {
          this.showResult('success', 'ສຳເລັດແລ້ວ', 'ບັນທຶກຂໍ້ມູນອຸປະກອນການຮຽນການສອນຮຽບຮ້ອຍແລ້ວ');
          await this.fetchData();
        }
      } catch (e) {
        this.showResult('error', 'ເກີດຂໍ້ຜິດພາດ', 'ບໍ່ສາມາດບັນທຶກຂໍ້ມູນໄດ້');
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.materials-container { animation: fadeIn 0.4s ease; max-width: 1200px; margin: 0 auto; }

/* Info card */
.info-card { background: white; border-radius: 16px; padding: 24px 28px; box-shadow: 0 2px 8px rgba(0,0,0,0.07); border-left: 4px solid var(--lao); }
.section-label { font-size: 13px; font-weight: 700; color: var(--lao); margin-bottom: 16px; display: flex; align-items: center; gap: 8px; }
.auto-field { background: #f1f5f9 !important; color: #64748b; }
.auto-badge { display: inline-block; font-size: 10px; font-weight: 700; background: #e0f2fe; color: #0284c7; border-radius: 4px; padding: 1px 6px; margin-top: 4px; }

.field-row-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 16px; }
.field-row-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 16px; }
.form-group { position: relative; }
.form-group label { display: block; font-size: 13px; color: #475569; margin-bottom: 6px; font-weight: 600; }
.form-group input, .form-group select, .form-group textarea {
  width: 100%; padding: 9px 11px; border: 1px solid #e2e8f0; border-radius: 8px;
  font-size: 14px; box-sizing: border-box; font-family: 'Phetsarath OT', inherit;
}
.form-group input:focus, .form-group select:focus, .form-group textarea:focus {
  outline: none; border-color: var(--lao); box-shadow: 0 0 0 3px rgba(0,63,138,0.1);
}

/* Level selector */
.level-selector-card { background: white; border-radius: 16px; padding: 22px 24px; box-shadow: 0 2px 8px rgba(0,0,0,0.07); }
.level-btns { display: flex; gap: 12px; flex-wrap: wrap; margin-top: 8px; }
.level-btn {
  display: flex; align-items: center; gap: 10px; padding: 13px 22px;
  border: 2px solid #e2e8f0; border-radius: 12px; background: #f8fafc;
  cursor: pointer; font-size: 14px; font-weight: 700; color: #475569;
  transition: 0.2s; font-family: 'Phetsarath OT', inherit;
}
.level-btn:hover { border-color: var(--lao); color: var(--lao); background: #eff6ff; }
.level-btn.active { border-color: var(--lao); background: var(--lao); color: white; }
.level-btn i { font-size: 16px; }

/* Panel */
.panel { background: white; border-radius: 16px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.07); }
.panel-head { display: flex; justify-content: space-between; align-items: center; padding: 18px 24px; border-bottom: 1px solid #f1f5f9; }
.panel-head h3 { margin: 0; font-size: 15px; color: var(--lao); display: flex; align-items: center; gap: 8px; }
.panel-sub { margin: 4px 0 0; font-size: 12px; color: #94a3b8; }
.panel-stats { display: flex; gap: 8px; flex-wrap: wrap; }
.panel-body { padding: 20px 24px; }
.stat-pill { padding: 5px 12px; border-radius: 20px; font-size: 12px; font-weight: 700; }
.stat-pill.good { background: #d1fae5; color: #065f46; }
.stat-pill.need { background: #fef3c7; color: #92400e; }
.stat-pill.get  { background: #dbeafe; color: #1e40af; }

/* Table */
.table-wrap { overflow-x: auto; }
.materials-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.materials-table th {
  background: #f8fafc; padding: 10px 12px; text-align: left;
  font-size: 12px; color: #64748b; border-bottom: 2px solid #e2e8f0;
  white-space: nowrap;
}
.materials-table th small { font-size: 10px; color: #94a3b8; display: block; font-weight: 400; }
.materials-table td { padding: 6px 10px; border-bottom: 1px solid #f1f5f9; vertical-align: middle; }
.materials-table tr:hover { background: #fafbff; }

.col-no     { width: 50px; }
.col-num    { width: 115px; text-align: center; }
.col-remain { width: 90px; text-align: center; }
.col-del    { width: 44px; }

/* Grade group header row */
.grade-header-row td {
  background: linear-gradient(90deg, #e0e7ff 0%, #f0f4ff 100%);
  color: #3730a3; font-weight: 700; font-size: 13px;
  padding: 9px 14px; border-top: 2px solid #c7d2fe;
}
.grade-header-row i { margin-right: 6px; }
.grade-subtotals {
  float: right; font-weight: 400; font-size: 12px; color: #475569;
}
.grade-subtotals strong { margin: 0 8px 0 3px; }

/* Add button per grade */
.add-grade-row td {
  padding: 5px 14px 8px; background: #fafbff; border-bottom: 2px solid #e2e8f0;
}
.btn-add-grade {
  background: none; border: 1px dashed #94a3b8; border-radius: 6px;
  color: #64748b; font-size: 12px; padding: 4px 12px; cursor: pointer;
  font-family: 'Phetsarath OT', inherit; transition: 0.15s;
}
.btn-add-grade:hover { border-color: var(--lao); color: var(--lao); background: #eff6ff; }
.btn-add-grade i { margin-right: 4px; }

.remain-pos  { color: #059669; }
.remain-zero { color: #94a3b8; }
.remain-neg  { color: #dc2626; }

.book-name { font-size: 14px; color: #334155; }
.name-input { width: 100%; padding: 7px 10px; border: 1px solid #e2e8f0; border-radius: 6px; font-size: 14px; box-sizing: border-box; font-family: 'Phetsarath OT', inherit; }
.name-input:focus { outline: none; border-color: var(--lao); }
.num-input { width: 100%; padding: 7px 8px; border: 1px solid #e2e8f0; border-radius: 6px; font-size: 14px; text-align: center; box-sizing: border-box; }
.num-input:focus { outline: none; border-color: var(--lao); box-shadow: 0 0 0 2px rgba(0,63,138,0.1); }

/* Low stock */
.low-stock { background: #fff7ed !important; }
.low-badge { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; color: #d97706; font-weight: 700; margin-left: 8px; background: #fef3c7; padding: 2px 8px; border-radius: 10px; }

/* Total row */
.total-row { background: #f0f9ff; }
.total-row td { padding: 12px; color: var(--lao); font-weight: 700; border-bottom: none; }

/* Buttons */
.btn-del { background: #fee2e2; color: #ef4444; border: none; width: 30px; height: 30px; border-radius: 50%; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; transition: 0.2s; font-size: 12px; }
.btn-del:hover { background: #ef4444; color: white; }

/* Save */
.btn-save { background: var(--lao); color: white; padding: 15px 50px; border-radius: 12px; font-weight: 700; border: none; cursor: pointer; transition: 0.3s; width: 100%; font-size: 16px; display: flex; align-items: center; justify-content: center; gap: 8px; font-family: 'Phetsarath OT', inherit; }
.btn-save:hover:not(:disabled) { background: #002d62; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,63,138,0.3); }
.btn-save:disabled { opacity: 0.6; cursor: not-allowed; }

.text-right { text-align: right; }
.text-center { text-align: center; }
.mt-4 { margin-top: 20px; }
.mb-4 { margin-bottom: 20px; }
.shadow-sm { box-shadow: 0 2px 8px rgba(0,0,0,0.07); }

/* Modal */
.modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.4); backdrop-filter: blur(4px); display: flex; justify-content: center; align-items: center; z-index: 9999; }
.custom-modal-card { background: white; width: 100%; max-width: 450px; border-radius: 16px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.15); overflow: hidden; animation: modalPop 0.3s ease; }
.result-modal { text-align: center; padding: 40px 30px; }
.modal-icon { font-size: 60px; margin-bottom: 20px; }
.modal-icon.success { color: #10b981; }
.modal-icon.error { color: #ef4444; }
.modal-footer-actions { margin-top: 24px; }
.btn-modal-primary { padding: 12px 30px; border: none; border-radius: 8px; cursor: pointer; font-weight: 700; background: var(--lao); color: white; font-size: 15px; }
.btn-modal-primary.error { background: #ef4444; }

.loading-state-full { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 100px 0; color: #64748b; gap: 16px; }

@keyframes modalPop { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

@media (max-width: 900px) {
  .field-row-4 { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 600px) {
  .field-row-3, .field-row-4 { grid-template-columns: 1fr; }
  .level-btns { flex-direction: column; }
  .panel-head { flex-direction: column; align-items: flex-start; gap: 10px; }
}
</style>
